import { Tooltip, TreeDataNode } from "antd";
import React from "react";

export const transformNodesToQA = (nodes: any, edges: any) => {
    const output: Question[] = [];
    const processedNodes = new Set<string>();

    if (!nodes || !Array.isArray(nodes)) {
        return [];
    }

    const questionNodes: any = nodes?.filter((node: any) =>
        node.type === "questionNode" && node.data.label === "parentNode"
    );

    for (const questionNode of questionNodes) {
        const question = processQuestionNode(questionNode, nodes, edges, processedNodes);
        if (question) {
            output?.push(question);
        }
    }

    function transformData(data: any) {
        return data.map((item: any) => {
            if (item?.answers?.length === 1) {
                const { answers, ...rest } = item;
                return {
                    ...rest,
                    answer: answers[0]
                };
            }
            return item;
        });
    }

    const finalOutput = transformData(output);

    function filterAnswers (data: any) {
        const filterRecursive = (answers: any): any => {
            const hasYesNo = answers?.some((answer: any) => answer?.type === "yesNo");

            if (hasYesNo) {
                return answers?.filter((answer: any) => answer?.type !== "descriptionNode" && answer?.type !== "stepsNode")
                    .map((answer: any) => {
                        if (answer?.nested_answer) {
                            return {
                                ...answer,
                                nested_answer: {
                                    question: answer?.nested_answer?.question,
                                    questionNodeId: answer?.nested_answer?.questionNodeId,
                                    answers: filterRecursive(answer?.nested_answer?.answers),
                                },
                            };
                        }
                        return answer;
                    });
            }

            return answers?.map((answer: any) => {
                if (answer?.nested_answer) {
                    return {
                        ...answer,
                        nested_answer: {
                            question: answer?.nested_answer?.question,
                            questionNodeId: answer?.nested_answer?.questionNodeId,
                            answers: filterRecursive(answer?.nested_answer?.answers),
                        },
                    };
                }
                return answer;
            });
        };

        return data?.map((flow: any) => {
            if (flow?.answers) {
                return {
                    ...flow,
                    questionNodeId: flow?.questionNodeId,
                    question: flow?.question,
                    answers: filterRecursive(flow?.answers),
                };
            }
            return flow;
        });
    }
    return filterAnswers(finalOutput);
};

function processQuestionNode(
    questionNode: any,
    nodes: any,
    edges: any,
    processedNodes: Set<string>
): Question | null {
    const question: Question = {
        question: questionNode?.data.content || "",
        questionNodeId: questionNode?.id
    };

    const connectedNodes = getAllConnectedNodes(questionNode?.id, nodes, edges);
    if (connectedNodes?.length > 0) {
        const answers = processConnectedNodes(connectedNodes, nodes, edges, processedNodes);
        if (answers?.length > 0) {
            question.answers = answers?.filter(answer => answer?.type === "yesNo" || answer?.type === "descriptionNode" || answer?.type === "stepsNode" || answer?.type === "linkNode");
        }
    }

    return question;
}

function getAllConnectedNodes(nodeId: string, nodes: any[], edges: any[]): any[] {
    const result = new Set<any>();
    const visited = new Set<string>();

    function traverse(currentId: string) {
        if (visited.has(currentId)) return;
        visited.add(currentId);

        const outgoingEdges = edges?.filter((edge) => edge?.source === currentId);
        for (const edge of outgoingEdges) {
            const targetNode = nodes.find((node) => node?.id === edge?.target);
            if (targetNode) {
                result.add(targetNode);
                traverse(targetNode?.id);
            }
        }
    }

    traverse(nodeId);
    return Array.from(result);
}

function processConnectedNodes(
    connectedNodes: any[],
    nodes: any,
    edges: any,
    processedNodes: Set<string>
): Answer[] {
    const answers: Answer[] = [];

    // Process choice nodes (yesNo answers)
    const choiceNodes = connectedNodes?.filter(node => node.type === "choiceNode");

    for (const node of choiceNodes) {
        if (processedNodes.has(node.id)) continue;
        processedNodes.add(node.id);

        const yesNoAnswer: any = {
            type: "yesNo",
            value: node.data.label as "yes" | "no",
            nodeId: node.id
        };

        const connectedToChoice = getAllConnectedNodes(node.id, nodes, edges);
        const qaNode = connectedToChoice.find(n => n.type === "qaNode");

        if (qaNode) {
            const connectedToQA = getAllConnectedNodes(qaNode.id, nodes, edges);
            const nestedQuestionNode = connectedToQA.find(n => n.type === "questionNode");

            if (nestedQuestionNode) {
                const nestedConnected = getAllConnectedNodes(nestedQuestionNode.id, nodes, edges);
                const nestedAnswers = processConnectedNodes(
                    nestedConnected,
                    nodes,
                    edges,
                    processedNodes
                );

                yesNoAnswer.nested_answer = {
                    question: nestedQuestionNode.data.content || "",
                    questionNodeId: nestedQuestionNode.id,
                    answers: nestedAnswers
                };
            }
        } else {
            const addButtonNode = connectedToChoice.find(n => n.type === "addButtonNode");

            if (addButtonNode) {
                const addButtonConnected = getAllConnectedNodes(addButtonNode.id, nodes, edges);
                const nestedDescriptionNode = addButtonConnected.find(n => n.type === "descriptionNode");
                const nestedStepsNode = addButtonConnected.find(n => n.type === "stepsNode");
                const uploadNode = addButtonConnected.find(n => n.type === "uploadNode");
                const nestedLinkNode = addButtonConnected.find(n => n.type === "linkNode");

                if (nestedDescriptionNode) {
                    const descriptionAnswer: DescriptionAnswer = {
                        type: "descriptionNode",
                        value: nestedDescriptionNode.data.content || "",
                        is_upload: false,
                        nodeId: nestedDescriptionNode.id
                    };

                    if (uploadNode) {
                        descriptionAnswer.is_upload = true;
                        descriptionAnswer.upload = uploadNode.data.content || "";
                        descriptionAnswer.uploadNodeId = uploadNode.id;
                    }

                    yesNoAnswer.nested_answer = {
                        answers: [descriptionAnswer]
                    };
                }

                if (nestedStepsNode) {
                    const stepsAnswer: StepsAnswer = {
                        type: "stepsNode",
                        value: nestedStepsNode.data.content || "",
                        is_upload: false,
                        nodeId: nestedStepsNode.id
                    };

                    if (uploadNode) {
                        stepsAnswer.is_upload = true;
                        stepsAnswer.upload = uploadNode.data.content || "";
                        stepsAnswer.uploadNodeId = uploadNode.id;
                    }

                    yesNoAnswer.nested_answer = {
                        answers: [stepsAnswer]
                    };
                }

                if (nestedLinkNode) {
                    const linkAnswer: LinkAnswer = {
                        type: "linkNode",
                        value: nestedLinkNode.data.content || "",
                        is_upload: false,
                        nodeId: nestedLinkNode.id
                    };

                    if (uploadNode) {
                        linkAnswer.is_upload = true;
                        linkAnswer.upload = uploadNode.data.content || "";
                        linkAnswer.uploadNodeId = uploadNode.id;
                    }

                    yesNoAnswer.nested_answer = {
                        answers: [linkAnswer]
                    };
                }
            }
        }

        answers?.push(yesNoAnswer);
    }

    // Process description nodes
    const descriptionNodes = connectedNodes?.filter(node => node.type === "descriptionNode");
    for (const node of descriptionNodes) {
        if (processedNodes.has(node.id)) continue;
        processedNodes.add(node.id);

        const descriptionAnswer: DescriptionAnswer = {
            type: "descriptionNode",
            value: node.data.content || "",
            is_upload: false,
            nodeId: node.id
        };

        const connectedToNode = getAllConnectedNodes(node.id, nodes, edges);
        const uploadNode = connectedToNode.find(n => n.type === "uploadNode");

        if (uploadNode) {
            descriptionAnswer.is_upload = true;
            descriptionAnswer.upload = uploadNode.data.content || "";
            descriptionAnswer.uploadNodeId = uploadNode.id;
        }

        answers?.push(descriptionAnswer);
    }

    // Process steps nodes
    const stepsNodes = connectedNodes?.filter(node => node.type === "stepsNode");
    for (const node of stepsNodes) {
        if (processedNodes.has(node.id)) continue;
        processedNodes.add(node.id);

        const stepsAnswer: StepsAnswer = {
            type: "stepsNode",
            value: node.data.content || "",
            is_upload: false,
            nodeId: node.id
        };

        const connectedToNode = getAllConnectedNodes(node.id, nodes, edges);
        const uploadNode = connectedToNode.find(n => n.type === "uploadNode");

        if (uploadNode) {
            stepsAnswer.is_upload = true;
            stepsAnswer.upload = uploadNode.data.content || "";
            stepsAnswer.uploadNodeId = uploadNode.id;
        }

        answers?.push(stepsAnswer);
    }

    // Process link nodes
    const linkNodes = connectedNodes?.filter(node => node.type === "linkNode");
    for (const node of linkNodes) {
        if (processedNodes.has(node.id)) continue;
        processedNodes.add(node.id);

        const linkAnswer: LinkAnswer = {
            type: "linkNode",
            value: node.data.content || "",
            is_upload: false,
            nodeId: node.id
        };

        const connectedToNode = getAllConnectedNodes(node.id, nodes, edges);
        const uploadNode = connectedToNode.find(n => n.type === "uploadNode");

        if (uploadNode) {
            linkAnswer.is_upload = true;
            linkAnswer.upload = uploadNode.data.content || "";
            linkAnswer.uploadNodeId = uploadNode.id;
        }

        answers?.push(linkAnswer);
    }

    return answers;
}

interface Question {
    question: string;
    questionNodeId?: string;
    answers?: Answer[];
    answer?: Answer;
}

interface BaseAnswer {
    type: string;
    value: string;
    nodeId?: string;
    is_upload?: boolean;
    upload?: string;
    uploadNodeId?: string;
}

interface YesNoAnswer extends BaseAnswer {
    type: "yesNo";
    value: "yes" | "no";
    nested_answer?: Question;
}

interface DescriptionAnswer extends BaseAnswer {
    type: "descriptionNode";
}

interface LinkAnswer extends BaseAnswer {
    type: "linkNode";
}

interface StepsAnswer extends BaseAnswer {
    type: "stepsNode";
}

type Answer = YesNoAnswer | DescriptionAnswer | StepsAnswer | LinkAnswer;

interface OriginalAnswer {
    type: string;
    value: string;
    nodeId: string;
    is_upload?: boolean;
    upload?: string;
    uploadNodeId?: string;
    nested_answer?: OriginalQuestion;
}

interface OriginalQuestion {
    question?: string;
    questionNodeId: string;
    answer?: OriginalAnswer;
    answers?: OriginalAnswer[];
}

function truncate(text: string, limit: number = 18): string {
    return text.length > limit
        ? `${text.slice(0, limit)}...`
        : text;
}

function extractFileName(url: string): string {
    return url.split('/').pop() || 'Unknown File';
}

function TruncatedText({ prefix, fullText, limit = 16 }: { prefix: string; fullText: string; limit?: number }) {
    const truncatedText = truncate(fullText, limit);
    const needsTooltip = fullText.length > limit;

    return needsTooltip ? (
        <span>{prefix}{truncatedText}</span>
    ) : (
        <span>{prefix}{fullText}</span>
    );
}

function convertAnswerToTreeNode(answer: OriginalAnswer, parentKey: string = ''): TreeDataNode {
    const shouldCreateAnswerNode = answer?.value && typeof answer?.value === 'string' && answer?.value?.trim() !== '';
    const key = `${parentKey}-${answer?.nodeId}`;
    const node: any = {
        key,
        title: shouldCreateAnswerNode ? (
            <TruncatedText prefix="Answer: " fullText={answer?.value || ""} />
        ) : null,
        metadata: {
            type: answer?.type,
            nodeId: answer?.nodeId,
            isUpload: answer?.is_upload,
            uploadUrl: answer?.upload,
            uploadNodeId: answer?.uploadNodeId
        }
    };

    if (shouldCreateAnswerNode) {
        if (answer?.is_upload && answer?.upload) {
            const fileName = extractFileName(answer?.upload);
            const uploadNode: any = {
                key: `${key}-upload`,
                title: (
                    <span>
                        Upload:&nbsp;
                        <a
                            href={answer?.upload}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="highlight-text"
                            onMouseEnter={(e) => (e.currentTarget.style.color = "blue")}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "white")}
                        >
                            {truncate(fileName.split('_').slice(1).join('_'))}
                        </a>
                    </span>
                ),
                metadata: {
                    uploadUrl: answer?.upload,
                    uploadNodeId: answer?.uploadNodeId
                }
            };

            node.children = node.children || [];
            node.children.push(uploadNode);
        }

        if (answer?.nested_answer) {
            const nestedNodes = convertQuestionToTreeNodes(answer?.nested_answer, key);
            node.children = node.children ? [...node.children, ...nestedNodes] : nestedNodes;
        }
    }
    return shouldCreateAnswerNode ? node : [node].filter(Boolean)[0];
}

function convertQuestionToTreeNodes(question: OriginalQuestion, parentKey: string = ''): TreeDataNode[] {
    const key = `${parentKey}-${question.questionNodeId}`;
    const shouldCreateQuestionNode = question.question && question.question.trim() !== '';

    if (shouldCreateQuestionNode) {
        const questionNode: any = {
            key,
            title: <TruncatedText prefix="Question: " fullText={question.question || ""} />,
            metadata: {
                questionNodeId: question.questionNodeId
            }
        };

        if (question.answers && question.answers.length > 0) {
            questionNode.children = question.answers.map(answer =>
                convertAnswerToTreeNode(answer, key)
            );
        } else if (question.answer) {
            questionNode.children = [convertAnswerToTreeNode(question.answer, key)];
        }

        return [questionNode];
    } else {
        if (question.answers && question.answers.length > 0) {
            return question.answers.map(answer =>
                convertAnswerToTreeNode(answer, parentKey)
            );
        }

        if (question.answer) {
            return [convertAnswerToTreeNode(question.answer, parentKey)];
        }
    }

    return [];
}

export function convertToAntdTree(data: OriginalQuestion[]): TreeDataNode[] {
    return data.flatMap((question: any) => convertQuestionToTreeNodes(question));
}

export const filterAndHighlightTree = (data: TreeDataNode[], term: string, expandKeys: React.Key[]): TreeDataNode[] => {
    return data.reduce((acc: TreeDataNode[], node: any) => {
        const filteredNode = { ...node };

        // Get node text with proper type checking
        let nodeText: string = '';

        if (React.isValidElement(node.title)) {
            // Handle upload nodes specifically
            if (node.title.props.children && Array.isArray(node.title.props.children)) {
                // For upload nodes that have multiple children (prefix + link)
                nodeText = node.title.props.children
                    .map((child: any) => {
                        if (typeof child === 'string') return child;
                        if (child.props && child.props.children) return child.props.children;
                        return '';
                    })
                    .join('');
            } else {
                // For regular question/answer nodes
                nodeText = (node.title.props.fullText || node.title.props.children || '').toString();
            }
        } else {
            nodeText = (node.title || '').toString();
        }

        // Only proceed with search if both nodeText and term are non-empty strings
        const searchTerm = term?.toLowerCase() || '';
        const normalizedNodeText = nodeText?.toLowerCase() || '';
        const matchesTerm = normalizedNodeText.includes(searchTerm);

        if (matchesTerm && term) {
            try {
                // Check if this is an upload node
                const isUploadNode = React.isValidElement(node.title) &&
                    node.title.props.children &&
                    Array.isArray(node.title.props.children);

                if (isUploadNode) {
                    // Preserve the upload node structure but highlight the filename
                    const originalTitle = node.title.props.children;
                    filteredNode.title = (
                        <span>
                            {originalTitle[0]} {/* "Upload: " text */}
                            <a
                                href={originalTitle[1].props.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="highlight-text"
                                onMouseEnter={(e) => (e.currentTarget.style.color = "blue")}
                                onMouseLeave={(e) => (e.currentTarget.style.color = "white")}
                            >
                                {originalTitle[1].props.children.split(new RegExp(`(${term})`, 'gi')).map(
                                    (part: string, index: number) =>
                                        part.toLowerCase() === term.toLowerCase()
                                            ? <mark key={index}>{part}</mark>
                                            : part
                                )}
                            </a>
                        </span>
                    );
                } else {
                    // Regular question/answer node highlighting
                    const parts = nodeText.split(new RegExp(`(${term})`, 'gi'));
                    filteredNode.title = (
                        <Tooltip title={nodeText}>
                            <span>
                                {parts.map((part: string, index: number) => {
                                    const normalizedPart = part?.toLowerCase() || '';
                                    const normalizedTerm = term?.toLowerCase() || '';
                                    return normalizedPart === normalizedTerm
                                        ? <mark key={index}>{part}</mark>
                                        : part;
                                })}
                            </span>
                        </Tooltip>
                    );
                }
                expandKeys.push(node.key);
            } catch (error) {
                console.error('Error processing node:', error);
                filteredNode.title = nodeText; // Fallback to original text
            }
        }

        if (node.children) {
            filteredNode.children = filterAndHighlightTree(node.children, term, expandKeys);
            if (filteredNode.children.length > 0) {
                expandKeys.push(node.key);
            }
        }

        if (matchesTerm || (filteredNode.children && filteredNode.children.length > 0)) {
            acc.push(filteredNode);
        }

        return acc;
    }, []);
};